const IDS_ENDPOINT = '/apps/lensza/wishlist-ids';
const ADD_ENDPOINT = '/apps/lensza/wishlist';
const REMOVE_ENDPOINT = '/apps/lensza/wishlist-remove';

// Shared across every <wishlist-button> on the page — the first one to connect
// kicks off the fetch, every other instance just awaits the same promise
// instead of firing its own request.
let idsPromise = null;

function isCustomerLoggedIn() {
  return document.body.dataset.customerLoggedIn === 'true';
}

function getWishlistIds() {
  if (!isCustomerLoggedIn()) return Promise.resolve(new Set());

  if (!idsPromise) {
    idsPromise = fetch(IDS_ENDPOINT)
      .then((r) => (r.ok ? r.json() : { product_ids: [] }))
      .then((data) => new Set((data.product_ids || []).map(String)))
      .catch(() => new Set());
  }
  return idsPromise;
}

let wishlistStrings = null;

function getWishlistStrings() {
  if (wishlistStrings) return wishlistStrings;
  const script = document.querySelector('[data-wishlist-strings]');
  try {
    wishlistStrings = script ? JSON.parse(script.textContent || '{}') : {};
  } catch {
    wishlistStrings = {};
  }
  return wishlistStrings;
}

function showWishlistToast(added) {
  if (!window.LenszaToast) return;
  const strings = getWishlistStrings();
  const message = added ? strings.addedMessage : strings.removedMessage;
  if (message) window.LenszaToast.show(message);
}

function openLoginPrompt() {
  const dialogModal = document.getElementById('DialogModal');
  const template = document.querySelector('[data-wishlist-login-prompt-template]');
  if (
    !dialogModal ||
    !template ||
    typeof dialogModal.setContent !== 'function' ||
    typeof dialogModal.openModal !== 'function'
  ) {
    return;
  }

  if (typeof dialogModal.setSize === 'function') dialogModal.setSize('small');
  dialogModal.setContent(template.innerHTML);
  dialogModal.openModal();
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this._productId = String(this.dataset.productId || '');
    this._toggle = this.querySelector('button');
    if (!this._productId || !this._toggle) return;

    this._handleClick = this._handleClick.bind(this);
    this._toggle.addEventListener('click', this._handleClick);

    this._onExternalUpdate = (event) => {
      const detail = event.detail || {};
      if (String(detail.productId) !== this._productId) return;
      this._setAdded(!!detail.added);
    };
    document.addEventListener('wishlist:updated', this._onExternalUpdate);

    getWishlistIds().then((ids) => {
      if (this.isConnected) this._setAdded(ids.has(this._productId));
    });
  }

  disconnectedCallback() {
    if (this._toggle) this._toggle.removeEventListener('click', this._handleClick);
    document.removeEventListener('wishlist:updated', this._onExternalUpdate);
  }

  _setAdded(added) {
    this.classList.toggle('is-added', added);
    this._toggle.setAttribute('aria-pressed', String(added));
  }

  _handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this._busy) return;

    if (!isCustomerLoggedIn()) {
      openLoginPrompt();
      return;
    }

    const wasAdded = this.classList.contains('is-added');
    const nowAdded = !wasAdded;
    this._busy = true;
    this._setAdded(nowAdded); // optimistic

    const endpoint = nowAdded ? ADD_ENDPOINT : REMOVE_ENDPOINT;
    const payload = nowAdded
      ? {
          shopify_product_id: Number(this._productId),
          product_handle: this.dataset.productHandle || '',
          product_title: this.dataset.productTitle || '',
        }
      : { shopify_product_id: Number(this._productId) };

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`wishlist request failed: ${r.status}`);
        document.dispatchEvent(
          new CustomEvent('wishlist:updated', {
            detail: { productId: this._productId, added: nowAdded },
          }),
        );
        if (nowAdded) {
          window.LenszaAnalytics?.publish(
            'wishlist_added',
            {
              items: [
                {
                  item_id: this._productId,
                  item_name: this.dataset.productTitle || undefined,
                },
              ],
            },
            { gaEventName: 'add_to_wishlist' },
          );
        }
        showWishlistToast(nowAdded);
      })
      .catch(() => {
        this._setAdded(wasAdded); // revert optimistic state
      })
      .finally(() => {
        this._busy = false;
      });
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this._onWishlistUpdated = (event) => {
      const detail = event.detail || {};
      if (detail.added) return; // only removals need to touch this page's grid
      this._removeItem(detail.productId);
    };
    document.addEventListener('wishlist:updated', this._onWishlistUpdated);

    this._fetchList();
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this._onWishlistUpdated);
  }

  _fetchList() {
    const endpoint = this.dataset.endpoint || '/apps/lensza/wishlist';

    fetch(endpoint)
      .then((r) => (r.ok ? r.json() : { items: [], total: 0 }))
      .then((data) => this._renderItems(data.items || []))
      .catch(() => this._renderItems([]))
      .finally(() => {
        this.removeAttribute('data-loading');
        this.removeAttribute('aria-busy');
      });
  }

  async _renderItems(items) {
    const grid = this.querySelector('[data-wishlist-grid]');
    const emptyEl = this.querySelector('[data-wishlist-empty]');
    if (!grid) return;

    if (!items.length) {
      grid.innerHTML = '';
      if (emptyEl) emptyEl.classList.remove('hidden');
      return;
    }
    if (emptyEl) emptyEl.classList.add('hidden');

    const sectionId = this.dataset.cardSectionId || 'wishlist-card';
    const shopifySectionId = `shopify-section-${sectionId}`;
    const handles = items.map((item) => item.product_handle).filter(Boolean);

    // Fetch all product cards in parallel via Shopify's Section Rendering API —
    // gets live price/availability/image instead of a stale denormalized snapshot.
    // Promise.all preserves order (most-recently-saved first).
    const responses = await Promise.all(
      handles.map((handle) =>
        fetch(`/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`, {
          headers: { Accept: 'text/html' },
        }).catch(() => null),
      ),
    );

    const markupList = await Promise.all(
      responses.map((res) => (res && res.ok ? res.text() : Promise.resolve(null))),
    );

    const parser = new DOMParser();
    const nodes = [];
    for (const markup of markupList) {
      if (!markup) continue;
      const parsed = parser.parseFromString(markup, 'text/html');
      const sectionRoot = parsed.getElementById(shopifySectionId);
      const node = (sectionRoot || parsed.body).querySelector('[data-wishlist-item]');
      if (node) nodes.push(node.cloneNode(true));
    }

    if (!nodes.length) {
      grid.innerHTML = '';
      if (emptyEl) emptyEl.classList.remove('hidden');
      return;
    }

    grid.replaceChildren(...nodes);
  }

  _removeItem(productId) {
    const grid = this.querySelector('[data-wishlist-grid]');
    if (!grid) return;

    const node = grid.querySelector(`[data-product-id="${CSS.escape(String(productId))}"]`);
    if (node) node.remove();

    if (!grid.querySelector('[data-wishlist-item]')) {
      const emptyEl = this.querySelector('[data-wishlist-empty]');
      if (emptyEl) emptyEl.classList.remove('hidden');
    }
  }
}

if (!customElements.get('wishlist-grid')) {
  customElements.define('wishlist-grid', WishlistGrid);
}
